'use client';

import Link from 'next/link';
import { UsersRound } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { dashboardToneClasses, formatDashboardNumber } from './analytics-dashboard-style';
import type { AnalyticsHubData } from './analytics-dashboard-types';

interface PeopleHealthWidgetProps {
  people: AnalyticsHubData['people'];
}

export function PeopleHealthWidget({ people }: PeopleHealthWidgetProps) {
  return (
    <Card className="h-full rounded-3xl border-[var(--app-border)] bg-[var(--app-surface)] shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          <span className="flex items-center gap-2">
            <UsersRound className="h-4 w-4 text-primary-600" />
            Люди
          </span>
          <Link href="/employees" className="text-xs font-medium text-primary-700 hover:underline">
            команда
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <div className="grid grid-cols-2 gap-2">
          <Metric label="Всего" value={people.total} />
          <Metric label="В графике" value={people.scheduled} />
          <Metric label="На смене" value={people.currently_on_shift} />
          <Metric label="Опоздали" value={people.late} />
        </div>

        <div className="rounded-2xl bg-[var(--app-surface-soft)] p-3">
          <div className="mb-2 flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-[var(--app-text)]">В фокусе</p>
            <span className="text-xs text-[var(--app-text-muted)]">{people.stuck} зависших</span>
          </div>
          {people.top_risk.length === 0 ? (
            <p className="py-4 text-center text-sm text-[var(--app-text-soft)]">Повторяющихся рисков по людям нет</p>
          ) : (
            <div className="space-y-2">
              {people.top_risk.slice(0, 4).map((person) => {
                const tone = dashboardToneClasses(person.tone);
                return (
                  <Link
                    key={person.id}
                    href={person.href}
                    className="flex items-center justify-between gap-3 rounded-2xl bg-[var(--app-surface)] px-3 py-2 transition hover:bg-white  "
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-[var(--app-text)]">{person.name}</span>
                      <span className="block truncate text-xs text-[var(--app-text-muted)]">{person.location_name || 'Без точки'}</span>
                    </span>
                    <span className={`shrink-0 rounded-full px-2 py-1 text-xs font-bold ${tone.soft}`}>
                      {person.risk_score}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-[var(--app-border)] px-3 py-2">
      <p className="text-xl font-black tabular-nums text-[var(--app-text)]">{formatDashboardNumber(value)}</p>
      <p className="text-[10px] text-[var(--app-text-muted)]">{label}</p>
    </div>
  );
}

